import {
  type DefaultError,
  type QueryClient,
  type QueryKey,
  type Query,
  type QueryMeta,
  type QuerySubscriptionOptions,
  type QuerySubscribeFn,
  isServer,
} from '@tanstack/react-query'
import {noop} from '@github-ui/noop'

type MaybeAsync<T> = T | Promise<T>

declare module '@tanstack/react-query' {
  export interface QuerySubscriptionContext<TQueryKey extends QueryKey = QueryKey> {
    client: QueryClient
    queryKey: TQueryKey
    meta: QueryMeta | undefined
  }

  export type QuerySubscribeFn<TData = unknown, TQueryKey extends QueryKey = QueryKey> = (
    data: TData,
    ctx: QuerySubscriptionContext<TQueryKey>,
  ) => MaybeAsync<void | (() => MaybeAsync<void>)>

  export interface QuerySubscriptionOptions<TData = unknown, TQueryKey extends QueryKey = QueryKey> {
    /**
     * By default, the subscription will only subscribe when the query initially mounts, and only unsubscribe when the
     * last observer unmounts (`stable`). To trigger resubscription whenever `subscribe` changes, set this to
     * `onChange`.
     *
     * **Caution**: if set to `onChange`, `subscribe` must be memoized (ie, with `useCallback`) to avoid frequent
     * resubscribing.
     *
     * @default 'stable'
     */
    resubscribeMode?: 'stable' | 'onChange'
    /**
     * Subscription function. Called on subscribe with the current query data and the context. May be async.
     * @return An unsubscribe function for cleaning up the subscription.
     */
    subscribe: QuerySubscribeFn<TData, TQueryKey>
  }

  export type QuerySubscription<TData = unknown, TQueryKey extends QueryKey = QueryKey> =
    | QuerySubscriptionOptions<TData, TQueryKey>
    | QuerySubscribeFn<TData, TQueryKey>

  interface QueryObserverOptions<
    TQueryFnData = unknown,
    // eslint-disable-next-line unused-imports/no-unused-vars
    TError = DefaultError,
    // eslint-disable-next-line unused-imports/no-unused-vars
    TData = TQueryFnData,
    TQueryData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
    // eslint-disable-next-line unused-imports/no-unused-vars
    TPageParam = never,
  > {
    /**
     * Attach a subscription for the active lifetime of this query. Subscriptions are effects that run when the query
     * first fetches data. They return a cleanup function which is called when the query data becomes stale or all
     * query observers are unmounted (whichever happens last). This way, subscriptions ensure that data that is marked
     * as fresh in the store remains fresh in reality. Subscriptions are globally deduplicated - if multiple query
     * observers are active, only the first mounted subscription will be attached.
     *
     * @note Subscriptions are an internal (GitHub only) functionality, not documented in the Tanstack docs.
     */
    subscription?: QuerySubscription<TQueryData, TQueryKey>
  }
}

const DEFAULT_RESUBSCRIBE_MODE = 'stable'

const normalizeSubscription = (subscription: QuerySubscriptionOptions | QuerySubscribeFn): QuerySubscriptionOptions =>
  typeof subscription === 'function'
    ? {
        subscribe: subscription,
      }
    : subscription

/** Sets up query subscription support for the passed client. */
export function startQuerySubscriptionManager(queryClient: QueryClient) {
  if (isServer) return noop // subscriptions are client-only

  type Entry = {
    subscription: QuerySubscriptionOptions
    unsubscribe?: () => MaybeAsync<void>
    staleTimer?: ReturnType<typeof setTimeout>
    lastStaleTime: number
    lastDataUpdatedAt?: number
  }

  const activeEntries = new Map<string, Entry>()
  const reconcileChains = new Map<string, Promise<unknown>>()

  /** Serialize work per query hash so that a resubscribe can't overlap the teardown it follows. */
  const queueQueryTask = (hash: string, work: () => MaybeAsync<void>) => {
    const previous = reconcileChains.get(hash)

    const run = async () => {
      await previous

      try {
        await work()
      } catch (error) {
        // Suppress errors so that a failed task doesn't cancel the entire queue
        setTimeout(() => {
          throw error
        })
      }

      if (reconcileChains.get(hash) === next) reconcileChains.delete(hash)
    }

    const next = run()
    reconcileChains.set(hash, next)
  }

  /** Unsubscribe and clean up a subscription. */
  const teardown = async (hash: string) => {
    const entry = activeEntries.get(hash)
    if (!entry) return

    clearTimeout(entry.staleTimer)

    try {
      await entry.unsubscribe?.()
    } finally {
      activeEntries.delete(hash)
    }
  }

  /** Find the longest window over which any observer would serve this data without refetching. */
  const maxStaleTime = (query: Query) => {
    const result = Math.max(
      ...query.observers.map(({options}) => {
        const resolved = typeof options.staleTime === 'function' ? options.staleTime(query) : (options.staleTime ?? 0)
        const staleTime = resolved === 'static' ? Infinity : resolved
        return staleTime
      }),
    )

    if (!isFinite(result) && !isFinite(query.gcTime) && !activeEntries.has(query.queryHash))
      // eslint-disable-next-line no-console
      console.warn(
        'Subscription configured for query with infinite `staleTime` and `gcTime`. This creates a long-lived subscription that is never cleaned up, resulting in memory leaks. Apply a smaller `staleTime` or `gcTime` to this query.',
      )

    return result
  }

  /** Set a timer to re-reconcile the query after the stale time completes. */
  const scheduleStaleCheck = (query: Query, staleTime: number) => {
    const hash = query.queryHash
    const entry = activeEntries.get(hash)
    if (!entry) return

    clearTimeout(entry.staleTimer)
    entry.staleTimer = undefined

    if (!isFinite(staleTime)) return
    const delay = query.state.dataUpdatedAt + staleTime - Date.now()
    if (delay <= 0) return

    entry.staleTimer = setTimeout(() => queueQueryTask(hash, () => reconcile(hash)), delay)
    entry.lastDataUpdatedAt = query.state.dataUpdatedAt
  }

  /** Re-check this query state and, if necessary, clean up and/or resubscribe. */
  const reconcile = async (hash: string) => {
    const query = queryClient.getQueryCache().get(hash)

    if (!query) {
      await teardown(hash)
      return
    }

    const currentEntry = activeEntries.get(hash)

    // Use the subscription options from the first mounted observer with a configured subscription,
    // falling back to the cached ones if every observer has unmounted.
    const optionsSubscription =
      query.observers.find(o => !!o.options.subscription)?.options.subscription ?? currentEntry?.subscription

    if (!optionsSubscription) return

    const subscription = normalizeSubscription(optionsSubscription)

    // Observer options are gone on unmount, so store the last largest stale time in case there are any updates (ie,
    // direct data writes) made after all observers unmount.
    const staleTime = query.observers.length > 0 ? maxStaleTime(query) : (currentEntry?.lastStaleTime ?? 0)
    const staleTimeChanged = staleTime !== currentEntry?.lastStaleTime
    if (currentEntry) currentEntry.lastStaleTime = staleTime

    // `isActive()` is false when there are no observers OR every observer has `enabled: false`, so disabling and
    // unmounting are the same thing here. Requiring `success` defers subscribing until the initial fetch resolves.
    // Data that is still fresh keeps the subscription open, because the next observer to mount would just
    // reuse it instead of refetching.
    const shouldBeOpen = query.state.status === 'success' && (query.isActive() || !query.isStaleByTime(staleTime))

    if (!shouldBeOpen) {
      await teardown(hash)
      return
    }

    const isStable = (subscription.resubscribeMode ?? DEFAULT_RESUBSCRIBE_MODE) === 'stable'
    const isSubscribed = currentEntry !== undefined
    const subscriptionChanged = subscription.subscribe !== currentEntry?.subscription.subscribe
    const dataWasUpdated = query.state.dataUpdatedAt > (currentEntry?.lastDataUpdatedAt ?? -1)

    // Stable or subscribe function didn't change: just restart stale timer if necessary
    if (isSubscribed && (isStable || !subscriptionChanged)) {
      if (dataWasUpdated || staleTimeChanged) scheduleStaleCheck(query, staleTime)
      return
    }

    await teardown(hash)

    const unsubscribe =
      (await subscription.subscribe(query.state.data, {
        client: queryClient,
        queryKey: query.queryKey,
        meta: query.meta,
      })) ?? undefined

    activeEntries.set(hash, {subscription, lastStaleTime: staleTime, unsubscribe})
    scheduleStaleCheck(query, staleTime)
  }

  const cache = queryClient.getQueryCache()

  for (const query of cache.getAll()) queueQueryTask(query.queryHash, () => reconcile(query.queryHash))

  const unsubscribe = cache.subscribe(event => {
    const hash = event.query.queryHash
    if (event.type === 'removed') queueQueryTask(hash, () => teardown(hash))
    else queueQueryTask(hash, () => reconcile(hash))
  })

  return () => {
    unsubscribe()
    for (const hash of activeEntries.keys()) queueQueryTask(hash, () => teardown(hash))
  }
}
